import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Mail, Lock, User as UserIcon, Phone, Gift } from 'lucide-react';

const Register = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [referralUsername, setReferralUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  // Auto-fill referral from URL parameter; fall back to persisted localStorage
  // value so referrals survive multi-page navigation (e.g. landing on a shared
  // Tarot card page first, then registering later).
  useEffect(() => {
    const refParam = searchParams.get('ref') || searchParams.get('coupon');
    let ref = refParam;
    if (!ref) {
      try { ref = localStorage.getItem('anant_ref') || ''; } catch (_) { ref = ''; }
    }
    if (ref) {
      // Coupon codes are 6-char alphanumeric — keep uppercase; usernames stay lowercase.
      const val = String(ref).trim();
      const isCoupon = /^[A-Za-z0-9]{6}$/.test(val) && /[A-Z]/i.test(val) && /\d/.test(val);
      setReferralUsername(isCoupon ? val.toUpperCase() : val.toLowerCase());
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(name, username, email, password, whatsappNumber, referralUsername);
      toast.success('Registration successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="glass-card p-8">
          <h1 className="text-4xl font-bold mb-2 text-center" data-testid="register-title">Create Account</h1>
          <p className="text-muted-foreground text-center mb-8">
            Join ANANT WEB PORTAL today
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">Full Name</label>
              <div className="relative">
                <UserIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all"
                  placeholder="John Doe"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Username (Compulsory)</label>
              <div className="relative">
                <UserIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  required
                  autoComplete="username"
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all"
                  placeholder="johndoe123"
                  data-testid="register-username-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all"
                  placeholder="you@example.com"
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">WhatsApp Number</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="tel"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  required
                  autoComplete="tel"
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all"
                  placeholder="+91 9876543210"
                  data-testid="register-whatsapp-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {searchParams.get('ref') || searchParams.get('coupon') ? (
                  <span className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-primary" />
                    Referral Username / Coupon Code
                    <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Auto-filled · +10 AT bonus 🎁</span>
                  </span>
                ) : (
                  'Referral Username or 6-digit Coupon (Optional)'
                )}
              </label>
              <div className="relative">
                <UserIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  value={referralUsername}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    // Preserve uppercase for 6-char coupon-like inputs, else lowercase username
                    setReferralUsername(/^[A-Za-z]{3}\d{0,3}$/.test(v) || /^[A-Z]{3}\d{3}$/.test(v.toUpperCase()) ? v.toUpperCase() : v.toLowerCase());
                  }}
                  autoComplete="off"
                  className={`w-full pl-12 pr-4 py-3 bg-white/5 border rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all ${searchParams.get('ref') || searchParams.get('coupon') ? 'border-primary/50' : 'border-white/10'}`}
                  placeholder="anant1981 or ANI042"
                  data-testid="register-referral-input"
                />
              </div>
              {searchParams.get('ref') || searchParams.get('coupon') ? (
                <p className="text-xs text-primary mt-1">🎁 Coupon applied — dono ko 10 AT bonus milega on signup!</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">Username ya 6-digit coupon code — dono kaam karenge. Bonus: signup pe 10 AT!</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all"
                  placeholder="••••••••"
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-button"
            >
              {loading ? 'Creating Account...' : 'Register'}
            </button>
          </form>

          <p className="text-center text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
              Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
